// slice.ts
//
// (c) NCR Voyix Corporation 2025. All rights reserved.

import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  DeviceDetailResponse,
  SiteDeviceData,
  SiteData,
  MissingDevicesData
} from '../../data/siteReadinessData'
import { RequestState } from '../common'
import { BreadcrumbSegment } from '../../components/BreadcrumbNavigation'
import {
  siteReadiness,
  siteReadinessDevices,
  getDeviceInfo,
  missingDevices
} from './thunks'
import { COMPANY_CHANGE, LOGOUT } from '../globalActions'

export interface SiteReadinessState {
  sites: SiteData[],
  siteReadinessRequestState: RequestState,
  selectedSite: SiteData | null,
  selectedDevice: SiteDeviceData | null,
  siteDevicesRequestState: RequestState,
  devices: SiteDeviceData[],
  deviceInfo: DeviceDetailResponse | null,
  deviceInfoRequestState: RequestState,
  missingDevicesRequestState: RequestState,
  missingDevices: MissingDevicesData | null,
  breadcrumbs: BreadcrumbSegment[]
}

const initialState: SiteReadinessState = {
  sites: [],
  siteReadinessRequestState: 'idle',
  selectedSite: null,
  selectedDevice: null,
  siteDevicesRequestState: 'idle',
  devices: [],
  deviceInfo: null,
  deviceInfoRequestState: 'idle',
  missingDevicesRequestState: 'idle',
  missingDevices: null,
  breadcrumbs: []
}

export const siteReadinessSlice = createSlice({
  name: 'siteReadiness',
  initialState,
  reducers: {
    setSiteReadinessRequestState: (
      state,
      action: PayloadAction<RequestState>
    ) => {
      state.siteReadinessRequestState = action.payload
    },
    setSiteDevicesRequestState: (
      state,
      action: PayloadAction<RequestState>
    ) => {
      state.siteDevicesRequestState = action.payload
    },
    setMissingDevicesRequestState: (
      state,
      action: PayloadAction<RequestState>
    ) => {
      state.missingDevicesRequestState = action.payload
    },
    setSelectedSite: (
      state,
      action: PayloadAction<SiteData | null>
    ) => {
      state.selectedSite = action.payload
    },
    setSelectedDevice: (
      state,
      action: PayloadAction<SiteDeviceData | null>
    ) => {
      state.selectedDevice = action.payload
    },
    setSelectedDeviceInfo: (
      state,
      action: PayloadAction<DeviceDetailResponse | null>
    ) => {
      state.deviceInfo = action.payload
    },
    setSiteReadinessBreadcrumbs: (state, action: PayloadAction<BreadcrumbSegment[]>) => {
      state.breadcrumbs = [...action.payload].sort((a, b) => a.order - b.order)
    },
    setSiteReadinessDevices: (
      state,
      action: PayloadAction<SiteDeviceData[]>
    ) => {
      state.devices = action.payload
    },
    setMissingDevices: (
      state,
      action: PayloadAction<MissingDevicesData | null>
    ) => {
      state.missingDevices = action.payload
    }
  },
  extraReducers: (builder) => {
    builder.addCase(siteReadiness.pending, (state) => {
      state.siteReadinessRequestState = 'pending'
    })
    builder.addCase(siteReadiness.rejected, (state) => {
      state.siteReadinessRequestState = 'rejected'
    })
    builder.addCase(siteReadiness.fulfilled, (state, action) => {
      state.sites = action.payload
      state.siteReadinessRequestState = 'fulfilled'
    })
    builder.addCase(siteReadinessDevices.pending, (state) => {
      state.siteDevicesRequestState = 'pending'
    })
    builder.addCase(siteReadinessDevices.rejected, (state) => {
      // * Note: Set the request state to pending so the site devices drawer table continues to spin for the new request.
      state.siteDevicesRequestState = 'pending'
    })
    builder.addCase(siteReadinessDevices.fulfilled, (state, action) => {
      state.devices = action.payload
      state.siteDevicesRequestState = 'fulfilled'
    })
    builder.addCase(getDeviceInfo.pending, (state) => {
      state.deviceInfoRequestState = 'pending'
    })
    builder.addCase(getDeviceInfo.rejected, (state) => {
      // * Note: Set the request state to pending so the devices drawer continues to show loading for the new request.
      state.deviceInfoRequestState = 'pending'
    })
    builder.addCase(getDeviceInfo.fulfilled, (state, action) => {
      state.deviceInfo = action.payload
      state.deviceInfoRequestState = 'fulfilled'
    })
    builder.addCase(missingDevices.pending, (state) => {
      state.missingDevicesRequestState = 'pending'
    })
    builder.addCase(missingDevices.rejected, (state) => {
      state.missingDevicesRequestState = 'pending'
    })
    builder.addCase(missingDevices.fulfilled, (state, action) => {
      state.missingDevices = action.payload
      state.missingDevicesRequestState = 'fulfilled'
    })

    // Reset state on logout
    builder.addCase(LOGOUT, () => initialState)
    builder.addCase(COMPANY_CHANGE, () => initialState)
  }
})

export default siteReadinessSlice.reducer
export const {
  setSiteReadinessRequestState,
  setSiteDevicesRequestState,
  setSelectedSite,
  setSelectedDevice,
  setSelectedDeviceInfo,
  setMissingDevices,
  setMissingDevicesRequestState,
  setSiteReadinessBreadcrumbs,
  setSiteReadinessDevices
} = siteReadinessSlice.actions
